import { DueDate } from "components/design/DueDate";
import ToolTip from "components/design/toolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FileInput } from "components/FileInput/FileInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { ToolTipIcon } from "components/icons/TooltipIcon";
import { LocalFilePreview, Preview } from "components/PreviewModal";
import { DISPLAY_DATE } from "constants/date";
import {
  FILING_INFO_MISSING,
  IN_REVIEW,
  NONE,
  NOT_STARTED,
  PENDING_DETAILS,
  PROCESSING,
  REGISTERED,
  VALIDATING,
} from "constants/salesTaxRegistrationStatusType";
import dayjs from "dayjs";
import { Field, FieldProps, FormikValues } from "formik";
import { AnyObjectSchema } from "yup";
import { addExistingRegistrationValidation } from "formValidations/addExistingRegistration";
import { addFilingDetailsValidation } from "formValidations/addFilingDetails";
import { addRegistrationAdminSchema } from "formValidations/addRegistrationSchema";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { ReactNode, useRef, useState } from "react";
import {
  CompleteFilingDetailsPayload,
  PostSalesRegistrationPayload,
  useCompleteFilingDetailsMutation,
  useCreatePostSalesRegistrationsMutation,
  useGetNonExemptedStateQuery,
  useGetSalesTaxRegistrationDetailsQuery,
  useUpdatePostSalesTaxRegistrationsMutation,
} from "store/apis/salesTaxRegistration";
import { BackendError } from "types/utils/error";
import { paymentScheduleMap } from "./RegistrationDetails";
import {
  getSelectedState,
  initialRegistrationDataType,
  paymentScheduleOption,
} from "./SalesTaxRegistrations";

const DueOptionItem = ({
  label,
  helperText,
}: {
  label: string;
  helperText?: string | ReactNode;
  hasArrow?: boolean;
}) => {
  return (
    <div className="t-flex t-justify-between t-w-full">
      <span className="t-text-body-sm t-text-text-100">{label}</span>

      {helperText && (
        <span className="t-text-caption t-text-text-30">{helperText}</span>
      )}
    </div>
  );
};
export const AddExistingRegistrationModal = ({
  onClose,
  onRegistered,
  initialRegistrationData,
}: {
  onClose: () => void;
  onRegistered?: (stateId: string, stateName: string) => void;
  initialRegistrationData?: initialRegistrationDataType;
}) => {
  const entityId = useCurrentEntityId();
  const { successToast, alertToast } = useToast();
  const privewModal = useModal();
  const { isAdmin } = useRoleBasedView();
  const [previewFile, setPreviewFile] = useState<File>();

  const {
    data: registrationDetailsData,
    isLoading,
    isSuccess,
    isFetching,
    refetch,
  } = useGetSalesTaxRegistrationDetailsQuery(
    {
      entityId,
      state_registration_id:
        initialRegistrationData?.state_registration_id || "",
    },
    {
      skip: !initialRegistrationData?.state_registration_id,
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: nonExemptedState, isLoading: isFetchingExemptedState } =
    useGetNonExemptedStateQuery({ entityId });

  const stateOptions = nonExemptedState?.map(({ uuid, state }) => ({
    value: uuid,
    label: state,
  }));

  const [createSalesTaxRegistration, { isLoading: isCreatingRegistration }] =
    useCreatePostSalesRegistrationsMutation();

  const [
    updatePostSalesTaxRegistration,
    { isLoading: isUpdatingRegistration },
  ] = useUpdatePostSalesTaxRegistrationsMutation();

  const [completeFilingDetails, { isLoading: isCompletingFilingDetails }] =
    useCompleteFilingDetailsMutation();

  const isFilingInfoMissing =
    registrationDetailsData?.status === FILING_INFO_MISSING;

  const handleSubmit = async (values: FormikValues) => {
    const payment_schedule = values.payment_schedule?.length
      ? values.payment_schedule
      : NONE;
    const wasAlreadyRegistered = registrationDetailsData?.status === REGISTERED;

    try {
      if (isFilingInfoMissing) {
        await completeFilingDetails({
          entityId,
          registrationId:
            initialRegistrationData?.state_registration_id as string,
          payload: {
            payment_schedule,
            filing_start_date: values.filing_start_date,
            sales_tax_id: values.sales_tax_id,
          } as CompleteFilingDetailsPayload,
        }).unwrap();
        successToast({
          message: `Filing details added for ${registrationDetailsData?.state}`,
        });
        refetch();
        onRegistered?.(
          initialRegistrationData?.state_id ?? "",
          registrationDetailsData?.state ?? ""
        );
      } else if (initialRegistrationData?.state_registration_id) {
        await updatePostSalesTaxRegistration({
          entityId,
          state_registration_id:
            initialRegistrationData?.state_registration_id as string,
          payload: {
            ...values,
            payment_schedule,
            status: REGISTERED,
          } as PostSalesRegistrationPayload,
        }).unwrap();
        successToast({
          message: `Registration ${
            isAdmin ? `details updated` : `completed`
          } for ${registrationDetailsData?.state}`,
        });
        refetch();
        if (!wasAlreadyRegistered) {
          onRegistered?.(
            initialRegistrationData?.state_id ?? "",
            registrationDetailsData?.state ?? ""
          );
        }
      } else {
        const { state_registration_id, ...restValues } = values;
        await createSalesTaxRegistration({
          entityId,
          payload: {
            ...restValues,
            payment_schedule,
            status: REGISTERED,
          } as PostSalesRegistrationPayload,
        }).unwrap();
        const selectedStateName = getSelectedState(
          values.state_id,
          stateOptions
        );
        successToast({
          message: `Registration completed for ${selectedStateName}`,
        });
        onRegistered?.(values.state_id, selectedStateName);
      }
      onClose();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const isRegistrationInProcess =
    registrationDetailsData?.status === PROCESSING;

  let modalTitle = "Add Existing Registration";
  if (initialRegistrationData?.state_registration_id) {
    modalTitle = "Registration details";
  }
  if (isFilingInfoMissing) {
    modalTitle = "Add filing details";
  }

  let validationSchema: AnyObjectSchema = addExistingRegistrationValidation;
  if (isAdmin) {
    validationSchema = addRegistrationAdminSchema;
  }
  if (isFilingInfoMissing) {
    validationSchema = addFilingDetailsValidation;
  }

  const payment_schedule = registrationDetailsData?.payment_schedule;
  const initialValues = {
    tax_detail_id: registrationDetailsData?.tax_detail_id ?? "",
    state_id: initialRegistrationData?.state_id ?? "",
    payment_schedule:
      payment_schedule && payment_schedule !== NONE ? payment_schedule : "",
    filing_start_date: registrationDetailsData?.filing_start_date || "",
    filing_due_date: registrationDetailsData?.filing_due_date || "",
    sales_tax_id: registrationDetailsData?.sales_tax_id ?? "",
    existingTaxAccount: isRegistrationInProcess ? "NO" : "YES",
    state_registration_id: initialRegistrationData?.state_registration_id || "",
    registration_license: registrationDetailsData?.registration_license
      ?.file_name
      ? registrationDetailsData?.registration_license
      : null,
    application: registrationDetailsData?.application?.file_id
      ? registrationDetailsData?.application
      : null,
  };
  const { uuid: groupId } = useCurrentGroupContext();

  const [previewFileId, setPreviewFileId] = useState<string | null>(null);

  const onPreview = ({ pdfFile, uuid }: { pdfFile: any; uuid?: string }) => {
    if (uuid) {
      setPreviewFileId(uuid);
      return;
    }
    try {
      setPreviewFile(pdfFile);
      privewModal.open();
    } catch (error: any) {
      alertToast({ message: error });
    }
  };
  const formikRef = useRef(null);

  return (
    <Modal.Root open onOpenChange={onClose}>
      <Modal.Content useCustomOverlay>
        <Async.Root
          isLoading={isLoading || isFetching}
          isSuccess={
            !initialRegistrationData?.state_registration_id || isSuccess
          }
          isEmpty={Boolean(
            initialRegistrationData?.state_registration_id &&
              !registrationDetailsData
          )}
        >
          {previewFile && (
            <LocalFilePreview
              isOpen={privewModal.isOpen}
              file={previewFile}
              close={privewModal.close}
            />
          )}
          <Preview
            showModal={Boolean(previewFileId)}
            closeModal={() => setPreviewFileId(null)}
            groupId={groupId}
            previewId={previewFileId}
          />

          <Async.Success>
            <FormikForm
              ref={formikRef}
              initialValues={{
                ...initialValues,
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ setFieldValue, values }) => {
                const dueDateOption = [
                  {
                    label: (
                      <DueOptionItem
                        label="Jan 20, 2025 "
                        helperText="After 20 days"
                      />
                    ),
                    value: "2025-01-20",
                  },
                  {
                    label: (
                      <DueOptionItem
                        label="Jan 25, 2025 "
                        helperText="After 25 days"
                      />
                    ),
                    value: "2025-01-25",
                  },
                  {
                    label: (
                      <DueOptionItem label="Last day of month after period ends" />
                    ),
                    value: "2025-01-31",
                  },
                  {
                    label: (
                      <DueOptionItem
                        helperText="Custom"
                        label={
                          values.filing_due_date &&
                          !["2025-01-31", "2025-01-20", "2025-01-25"].includes(
                            values.filing_due_date
                          )
                            ? dayjs(values.filing_due_date).format(DISPLAY_DATE)
                            : "Custom"
                        }
                      />
                    ),
                    value: "CUSTOM",
                  },
                ];

                return (
                  <>
                    <Modal.Header>
                      <Modal.Title>
                        {modalTitle}{" "}
                        {initialRegistrationData?.state &&
                          `(${initialRegistrationData?.state})`}
                      </Modal.Title>
                      <Modal.Close />
                    </Modal.Header>
                    <Modal.Body className="t-max-h-[60vh] t-overflow-y-scroll ">
                      <div className="t-flex t-gap-y-3 t-flex-col ">
                        {!initialRegistrationData?.state_registration_id &&
                          !initialRegistrationData?.state_id && (
                            <Combobox
                              withForm
                              size="regular"
                              label="State"
                              name={`state_id`}
                              options={stateOptions}
                              isClearable={false}
                              isLoading={isFetchingExemptedState}
                              menuPortalTarget={document.body}
                            />
                          )}

                        <TextInput
                          label="Sales Tax ID"
                          placeholder="xxx-xx-xxxx"
                          name="sales_tax_id"
                          defaultValue={values.sales_tax_id}
                          required
                        />
                        <Combobox
                          withForm
                          size="regular"
                          label="Payment schedule"
                          name={`payment_schedule`}
                          defaultValue={
                            payment_schedule && payment_schedule !== NONE
                              ? {
                                  label:
                                    paymentScheduleMap[
                                      registrationDetailsData?.payment_schedule
                                    ],
                                  value:
                                    registrationDetailsData?.payment_schedule,
                                }
                              : null
                          }
                          required
                          options={paymentScheduleOption}
                          isClearable={false}
                          menuPortalTarget={document.body}
                        />

                        <Field name="filing_start_date">
                          {({ field }: FieldProps) => {
                            return (
                              <DateInput
                                {...field}
                                required
                                label="Filing start date"
                                placeholder={DISPLAY_DATE}
                                disabled={isRegistrationInProcess}
                                portalId="validOnDate"
                              />
                            );
                          }}
                        </Field>
                        {isAdmin && (
                          <DueDate
                            name="filing_due_date"
                            options={dueDateOption}
                            dueDate={values.filing_due_date}
                            required={!isAdmin}
                            disabled={Boolean(
                              registrationDetailsData?.filing_due_date &&
                                !isAdmin
                            )}
                            label="Filing due date (if filing period ends on Dec 31, 2024)"
                          />
                        )}

                        {Boolean(
                          (!registrationDetailsData?.sales_tax_id &&
                            !isRegistrationInProcess) ||
                            isAdmin
                        ) && (
                          <div>
                            <p className="t-text-text-30 t-text-body-sm t-mb-3 t-flex t-items-center t-gap-1">
                              Do you have existing tax account ID in this state?{" "}
                              <ToolTip
                                text={
                                  <span>
                                    e.g. Franchise Tax ID, Withholding <br />
                                    Tax ID, SOS ID, Payroll Tax ID, etc.
                                  </span>
                                }
                              >
                                <div className="-t-mt-0.5">
                                  <ToolTipIcon />
                                </div>
                              </ToolTip>
                            </p>

                            <RadioGroup.Root
                              onValueChange={(value) => {
                                setFieldValue("existingTaxAccount", value);
                              }}
                              className="t-flex t-flex-row t-gap-3 t-text-body-sm"
                              defaultValue={values?.existingTaxAccount}
                            >
                              <RadioGroup.Item
                                value="YES"
                                className="t-max-w-14"
                              >
                                Yes
                              </RadioGroup.Item>
                              <RadioGroup.Item value="NO">No</RadioGroup.Item>
                            </RadioGroup.Root>
                          </div>
                        )}
                        {values.existingTaxAccount === "YES" &&
                          (!registrationDetailsData?.sales_tax_id ||
                            isAdmin) && (
                            <TextInput
                              required={!isAdmin}
                              label="Existing tax accounts ID in this state"
                              note={
                                !isRegistrationInProcess && (
                                  <span className="t-text-neutral-40">
                                    e.g. EIN, SOS ID, Reseller Certificate
                                    Number
                                  </span>
                                )
                              }
                              disabled={isRegistrationInProcess}
                              name="tax_detail_id"
                            />
                          )}
                        {!isFilingInfoMissing && (
                          <>
                            <FileInput
                              name="application"
                              withForm
                              label="Registration application summary"
                              file={
                                values?.application?.file_id
                                  ? {
                                      uuid: values?.application?.file_id,
                                      name: values?.application?.file_name,

                                      file_type: "PDF",
                                      is_previewable: true,
                                    }
                                  : null
                              }
                              onFileClick={({ uuid }) => {
                                onPreview({
                                  pdfFile: values.application,
                                  uuid,
                                });
                              }}
                              onDelete={() => {}}
                              accept={{
                                "application/pdf": [".pdf"],
                              }}
                            />
                            <FileInput
                              name="registration_license"
                              required={!isAdmin}
                              withForm
                              file={
                                values?.registration_license?.file_id
                                  ? {
                                      uuid: values?.registration_license
                                        ?.file_id!,
                                      name: values?.registration_license
                                        ?.file_name!,
                                      file_type: "PDF",
                                      is_previewable: true,
                                    }
                                  : null
                              }
                              onFileClick={({ uuid }) => {
                                onPreview({
                                  pdfFile: values.registration_license,
                                  uuid,
                                });
                              }}
                              label="Sales Tax registration license"
                              onDelete={() => {}}
                              accept={{
                                "application/pdf": [".pdf"],
                              }}
                            />
                          </>
                        )}
                        {isRegistrationInProcess && (
                          <p className="t-text-text-30 t-flex t-gap-x-2 t-items-start t-text-body-sm t-m-0">
                            <ToolTipIcon />
                            Your Sales Tax license will be sent to your mailing
                            address upon successful <br /> registration. This
                            usually takes 1-2 weeks.
                          </p>
                        )}
                      </div>
                    </Modal.Body>
                    <Modal.FooterButtonGroup>
                      <Button onClick={onClose}>Cancel</Button>
                      <Button
                        type="submit"
                        isLoading={
                          isUpdatingRegistration ||
                          isCreatingRegistration ||
                          isCompletingFilingDetails
                        }
                        customType="primary"
                      >
                        {Boolean(
                          registrationDetailsData?.status === VALIDATING ||
                            registrationDetailsData?.status === IN_REVIEW
                        ) && "Approve"}
                        {Boolean(
                          registrationDetailsData?.status === REGISTERED
                        ) && "Update"}

                        {Boolean(
                          registrationDetailsData?.status === NOT_STARTED ||
                            registrationDetailsData?.status === PROCESSING ||
                            registrationDetailsData?.status ===
                              PENDING_DETAILS ||
                            !registrationDetailsData?.status
                        ) && "Add"}
                        {isFilingInfoMissing && "Save filing details"}
                      </Button>
                    </Modal.FooterButtonGroup>
                  </>
                );
              }}
            </FormikForm>
          </Async.Success>
        </Async.Root>
      </Modal.Content>
    </Modal.Root>
  );
};
